const DEPLOYMENT_CHANNEL = "artis-deployment";
const DISMISS_TYPE = "dismiss-update";

export type UpdatePromptState = "hidden" | "toast" | "button";

type DeploymentSource = {
  subscribe: (cb: (state: { isOutdated: boolean }) => void) => () => void;
};

export type UpdatePrompt = {
  get: () => UpdatePromptState;
  subscribe: (cb: () => void) => () => void;
  dismiss: () => void;
  update: () => void;
  dispose: () => void;
};

export const createUpdatePrompt = ({
  deployment,
  restart,
}: {
  deployment: DeploymentSource;
  restart: () => void;
}): UpdatePrompt => {
  let dismissed = false;
  const subs = new Set<() => void>();

  let outdated = false;
  const get = (): UpdatePromptState => (outdated ? (dismissed ? "button" : "toast") : "hidden");

  let last: UpdatePromptState = "hidden";
  const notify = () => {
    const next = get();
    if (next === last) return;
    last = next;
    for (const cb of subs) cb();
  };

  const unsubscribeDeployment = deployment.subscribe((state) => {
    outdated = state.isOutdated;
    notify();
  });

  const channel = new BroadcastChannel(DEPLOYMENT_CHANNEL);
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (data?.type !== DISMISS_TYPE || !outdated) return;
    dismissed = true;
    notify();
  };
  channel.addEventListener("message", onMessage);

  return {
    get,
    subscribe: (cb) => {
      subs.add(cb);
      return () => void subs.delete(cb);
    },
    dismiss: () => {
      if (get() !== "toast") return;
      dismissed = true;
      channel.postMessage({ type: DISMISS_TYPE });
      notify();
    },
    update: restart,
    dispose: () => {
      unsubscribeDeployment();
      channel.removeEventListener("message", onMessage);
      channel.close();
    },
  };
};
